import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import * as _ from "lodash";
import {RouteModel} from "../../../models/route.model";
import {RoutesViewSettingsRoutesModel} from "../../../models/routes-view-settings.model";
import {RouteStatusEnum} from "../../../enums/route-status.enum";
import {TaskPurposeType} from "../../../../commons/types/task-purpose.type";
import {TaskType} from "../../../../tasks/types/task.type";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {
  TaskRelationsPopupComponent,
  TaskRelationsPopupData
} from "../../../../commons/angular/components/task-relations-popup/task-relations-popup.component";
import {TasksHttpService} from "../../../../tasks/angular/services/tasks-http.service";
import {dedupeRelationsByService} from "../../../../tasks/utils/task-relations.util";
import {TaskRelationModel} from "../../../../tasks/models/task-relation.model";

require('./routes-table-list-row.scss');
@Component({
  standalone: false,
  selector: 'app-routes-table-list-row',
  templateUrl: './routes-table-list-row.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutesTableListRowComponent implements OnInit, OnChanges {
  public isShowCopyBtn: boolean = false;

  @Input() route: RouteModel;
  @Input() settings: RoutesViewSettingsRoutesModel;
  @Input() isSelected: boolean;
  @Input() isVisible: boolean;
  @Input() isResourceView: boolean = false;
  @Input() excludeMode: boolean = false;
  @Input() allPanelIds: string[] = [];
  @Input() panelId: string;
  @Input() isSummaryRow: boolean = false;

  @Output() onToggleSelected = new EventEmitter<{ route: RouteModel, isSelected: boolean }>();
  @Output() onToggleVisible = new EventEmitter<{ route: RouteModel, isVisible: boolean }>();
  @Output() onToggleLocked = new EventEmitter<RouteModel>();
  @Output() onSelectRouteResource = new EventEmitter<{ route: RouteModel, event: any }>();
  @Output() onRowClick = new EventEmitter<{ route: RouteModel, event: any }>();
  @Output() onRowDoubleClick = new EventEmitter<{ route: RouteModel, event: any }>();
  @Output() onDrop = new EventEmitter<{ route: RouteModel, event: any }>();
  @Output() onDrillDownClick = new EventEmitter<{ route: RouteModel, event: any }>();

  public TaskPurposeType = TaskPurposeType;
  public routeStatusType = RouteStatusEnum;

  public dropListId: string;
  public pickingTime: { time: number };
  public stopsCount: {pickup: number, dropoff: number, operation: number, destination: number} = {pickup: 0, dropoff: 0, operation: 0, destination: 0};
  public proNumbers: string[] = [];
  public relations: TaskRelationModel[] = [];
  public accountReferences: any[] = [];
  public volumeCapacity: number;
  public weightSeverity: string;
  public volumeSeverity: string;
  public palletsSeverity: string;

  public weightUnit: string;
  public volumeUnit: string;
  public severityThresholds: { low: number; mid: number; } = { low: 80, mid: 99 };

  constructor(
    @Inject('splitService') private splitService: any,
    @Inject(PanelService) private panelService: PanelService,
    @Inject(TasksHttpService) private tasksHttpService: TasksHttpService) {
    this.weightUnit = this.splitService.getShortWeightUnit();
    this.volumeUnit = this.splitService.getVolumeUnit();
  }

  ngOnInit() {
    this._precomputeRouteHelpers();
    this._setDropListId();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.route) {
      this._precomputeRouteHelpers();
    }
    if (changes.route || changes.panelId || changes.isSummaryRow) {
      this._setDropListId();
    }
  }

  // Ids are scoped to the panel: the same route is rendered by the routes table and by the
  // route drill-down summary row, and duplicate CdkDropList ids break connectedTo resolution.
  private _setDropListId() {
    this.dropListId = this.isSummaryRow ?
      `${this.panelId}-summary-${this.route?.id}` :
      `${this.panelId}-${this.route?.id}`;
  }

  private _precomputeRouteHelpers() {
    if (!this.route) {
      return;
    }

    // pickingTime
    this.pickingTime = {
      time: _.get(this.route, 'routeTaskList[0].routeAnalysis.eta', this.route.notBefore) - ((this.route.pickingTimeOffsetInMinutes || 0) * 60000)
    };

    // stopsCount
    let pickups = 0;
    let dropoffs = 0;
    let operations = 0;
    for (const task of this.route.routeTaskList || []) {
      const purpose = task.taskSpec?.purpose;
      if (purpose === TaskPurposeType.Pickup) pickups++;
      if (purpose === TaskPurposeType.Dropoff) dropoffs++;
      if (purpose === TaskPurposeType.Operation) operations++;
    }
    this.stopsCount = {
      pickup: pickups,
      dropoff: dropoffs,
      operation: operations,
      destination: dropoffs + operations
    };

    // proNumbers
    this.proNumbers = Array.from((this.route.routeTaskList || []).reduce((acc, task) => {
      if (task.serviceInfo?.proNumber) {
        acc.add(task.serviceInfo.proNumber);
      }
      return acc;
    }, new Set<string>()));

    // relations
    this.relations = dedupeRelationsByService(
      _.flatMap(this.route.routeTaskList || [], task => task.routeTaskRelations || []),
      TaskPurposeType.Dropoff
    );

    // volumeCapacity and severities
    const { totals, maxLoad } = this.route;
    const dimensions = maxLoad?.dimensions;
    const volCap = dimensions?.cube ?? ((dimensions?.length ?? 0) * (dimensions?.width ?? 0) * (dimensions?.height ?? 0));
    this.volumeCapacity = volCap;

    if (totals) {
      this.weightSeverity = this._getSeverity(totals.totalLoad, maxLoad?.weight ?? 0);
      this.volumeSeverity = this._getSeverity(totals.totalVolume, volCap);
      this.palletsSeverity = this._getSeverity(totals.totalPallets, maxLoad?.numberOfPallets ?? 0);
    }

    // accounts references
    if (!this.route.accountReferences) {
      this.accountReferences = [];
    } else {
      let hasDropoffs = false;
      if (this.route.routeTaskList) {
        hasDropoffs = this.route.routeTaskList.some((t: any) => t.taskSpec?.purpose === TaskPurposeType.Dropoff);
      }

      const filteredTasks = this.route.routeTaskList
        ? this.route.routeTaskList.filter((t: any) => {
            return hasDropoffs ? t.taskSpec?.purpose === TaskPurposeType.Dropoff : t.taskSpec?.purpose !== TaskPurposeType.Dropoff;
          })
        : [];

      this.accountReferences = this.route.accountReferences.map(accRef => {
        const clonedAccRef: any = { ...accRef };
        let palletsSum = 0;

        filteredTasks.forEach((t: any) => {
          if (t.taskType === TaskType.BundledStopover && t.bundledRouteTaskList) {
            t.bundledRouteTaskList.forEach((ct: any) => {
              if (ct.accountReference?.id === clonedAccRef.id) {
                palletsSum += this._getPalletsFromPackageList(ct.packageList);
              }
            });
          } else {
            if (t.accountReference?.id === clonedAccRef.id) {
              palletsSum += this._getPalletsFromPackageList(t.packageList);
            }
          }
        });

        clonedAccRef.pallets = Number.isInteger(palletsSum) ? palletsSum : Number(palletsSum.toFixed(2));
        return clonedAccRef;
      });

      this._sortAccountsByTaskOrder(filteredTasks);
    }
  }

  onRelationsClick(event: MouseEvent) {
    const taskIds = _.map(_.filter(this.route.routeTaskList, task => !_.isEmpty(task.routeTaskRelations)), 'id');
    this.panelService.open<TaskRelationsPopupComponent>(TaskRelationsPopupComponent, event, {
      relations: this.relations,
      tasks$: this.tasksHttpService.getTasks(taskIds)
    } as TaskRelationsPopupData);
  }

  private _sortAccountsByTaskOrder(tasks) {
    const firstIndexById = new Map<string, number>();

    const registerAccountId = (accountId: string, index: number) => {
      if (accountId && !firstIndexById.has(accountId)) {
        firstIndexById.set(accountId, index);
      }
    };

    (tasks || []).forEach((t: any, index: number) => {
      if (t.taskType === TaskType.BundledStopover && t.bundledRouteTaskList) {
        t.bundledRouteTaskList.forEach((ct: any) => registerAccountId(ct.accountReference?.id, index));
      } else {
        registerAccountId(t.accountReference?.id, index);
      }
    });

    this.accountReferences.sort((a, b) => {
      const aIndex = firstIndexById.has(a.id) ? firstIndexById.get(a.id) : Infinity;
      const bIndex = firstIndexById.has(b.id) ? firstIndexById.get(b.id) : Infinity;
      return aIndex - bIndex;
    });
  }

  private _getPalletsFromPackageList(packageList: any[]): number {
    let totalPallets = 0;
    if (Array.isArray(packageList) && packageList.length) {
      packageList.forEach(pkg => {
        if (pkg?.items?.length) {
          totalPallets += pkg.items.reduce((acc, item) => {
            if (item.pallets) {
              acc += (item.pallets || 0);
            }
            return acc;
          }, 0);
        }
      });
    }
    return Number.isInteger(totalPallets) ? totalPallets : Number(totalPallets.toFixed(2));
  }

  private _getSeverity(value: number, capacity?: number): string {
    if (!capacity) {
      return '';
    }
    const ratio = (value / capacity) * 100;
    return (ratio < this.severityThresholds.low) ? 'low' : ((ratio >= this.severityThresholds.low && ratio < this.severityThresholds.mid) ? 'mid' : 'high');
  }
}
